import { PersonListItemType } from '@api';
import { ActionsCell, EmailAnchor, PhoneNumberAnchor } from '@common';
import { DataTableColumn } from 'mantine-datatable';
import { useTranslation } from 'react-i18next';
import { FirstnameCell } from './components';

const TRANS_PATH = 'TABLE.COLUMNS';

type UsePersonsOverviewTableColumnsProps = {
  handleDeletePerson: (record: PersonListItemType) => void;
  handleToggleExpand: (record: PersonListItemType) => void;
  expandedPersonIds: string[];
};

type UsePersonsOverviewTableColumnsReturnType = {
  columns: DataTableColumn<PersonListItemType>[];
};

export const usePersonsOverviewTableColumns = ({
  handleDeletePerson,
  handleToggleExpand,
  expandedPersonIds,
}: UsePersonsOverviewTableColumnsProps): UsePersonsOverviewTableColumnsReturnType => {
  const { t } = useTranslation('persons');
  const noDataPlaceholder = t('NO_DATA_PLACEHOLDER', { ns: 'common' });

  const columns: DataTableColumn<PersonListItemType>[] = [
    {
      accessor: 'firstname',
      title: t(`${TRANS_PATH}.FIRSTNAME`),
      width: 200,
      sortable: true,
      render: (record) => (
        <FirstnameCell
          record={record}
          handleToggleExpand={handleToggleExpand}
          isExpanded={expandedPersonIds.includes(record.id)}
        />
      ),
    },
    {
      accessor: 'lastname',
      title: t(`${TRANS_PATH}.LASTNAME`),
      width: 150,
      sortable: true,
    },
    {
      accessor: 'contact.phone',
      title: t(`${TRANS_PATH}.PHONE`),
      render: (record) =>
        record.contact.phone ? (
          <PhoneNumberAnchor phoneNumber={record.contact.phone} />
        ) : (
          noDataPlaceholder
        ),
      width: 150,
    },
    {
      accessor: 'contact.mobile',
      title: t(`${TRANS_PATH}.MOBILE`),
      render: (record) =>
        record.contact.mobile ? (
          <PhoneNumberAnchor phoneNumber={record.contact.mobile} />
        ) : (
          noDataPlaceholder
        ),
      width: 150,
    },
    {
      accessor: 'contact.email',
      title: t(`${TRANS_PATH}.EMAIL`),
      render: (record) =>
        record.contact.email ? (
          <EmailAnchor email={record.contact.email} />
        ) : (
          noDataPlaceholder
        ),
      width: 300,
    },
    {
      accessor: 'companyName',
      title: t(`${TRANS_PATH}.COMPANY_NAME`),
      render: (record) => record.companyName || noDataPlaceholder,
      width: 300,
    },
    {
      accessor: 'actions',
      title: t(`${TRANS_PATH}.ACTIONS`),
      titleStyle: { textAlign: 'center' },
      render: (record) => (
        <ActionsCell.Root>
          <ActionsCell.Details to={`/persons/${record.id}`} />
          <ActionsCell.Delete onClick={() => handleDeletePerson(record)} />
        </ActionsCell.Root>
      ),
      width: 100,
    },
  ];

  return { columns };
};
